import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ============ Request/Response Types ============

interface User {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    emailVerified: boolean;
    activeStatus: string;
    imgUrl?: string;
    credits?: number;
    onboardingCompleted?: boolean;
    currentOnboardingStep?: number;
    createdAt: string;
    updatedAt: string;
}

interface UpdateProfileParams {
    firstName?: string;
    lastName?: string;
}

// Backend returns { success, message, user } format
interface ProfileResponse {
    success: boolean;
    message: string;
    user: User;
}

// ============ API Definition ============

export const userApi = createApi({
    reducerPath: 'userApi',
    baseQuery: fetchBaseQuery({
        // Use same baseUrl pattern as authApi - includes /auth
        baseUrl: `${process.env.NEXT_PUBLIC_API_URL}/auth`,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as any).auth.token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['User'],
    endpoints: (builder) => ({
        // Get own profile - returns { success, message, user }
        getProfile: builder.query<ProfileResponse, void>({
            query: () => '/info',
            providesTags: ['User'],
        }),

        // Update profile details (firstName, lastName) - returns { success, message, user }
        updateProfile: builder.mutation<ProfileResponse, UpdateProfileParams>({
            query: (body) => ({
                url: '/edit-profile',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['User'],
        }),

        // Upload profile image (field name is 'file' for deckwale) - returns { success, message, user }
        uploadProfileImage: builder.mutation<ProfileResponse, File>({
            query: (file) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: '/edit-profile',
                    method: 'PUT',
                    body: formData,
                    formData: true,
                };
            },
            invalidatesTags: ['User'],
        }),
    }),
});

export const {
    useGetProfileQuery,
    useUpdateProfileMutation,
    useUploadProfileImageMutation,
} = userApi;

export type { User, UpdateProfileParams, ProfileResponse };
